<template>
  <div class="how-it-works">
    <!-- Tab Selector -->
    <div class="tab-bar" role="tablist">
      <button
        class="tab-btn"
        :class="{ 'tab-btn--active': activeTab === 'video' }"
        role="tab"
        :aria-selected="activeTab === 'video'"
        @click="selectTab('video')"
      >
        <v-icon class="tab-icon">mdi-play-circle-outline</v-icon>
        Watch the video
      </button>
      <button
        class="tab-btn"
        :class="{ 'tab-btn--active': activeTab === 'business' }"
        role="tab"
        :aria-selected="activeTab === 'business'"
        @click="selectTab('business')"
      >
        <v-icon class="tab-icon">mdi-store-outline</v-icon>
        For your business
      </button>
      <button
        class="tab-btn"
        :class="{ 'tab-btn--active': activeTab === 'customers' }"
        role="tab"
        :aria-selected="activeTab === 'customers'"
        @click="selectTab('customers')"
      >
        <v-icon class="tab-icon">mdi-account-group-outline</v-icon>
        For your customers
      </button>
    </div>

    <!-- ── Video Tab ── -->
    <div v-if="activeTab === 'video'" class="tab-content tab-content--video">
      <!-- Shown if the video file can't be loaded, so the tab never looks broken. -->
      <div v-if="videoUnavailable" class="video-placeholder">
        <v-icon class="video-placeholder__icon">mdi-video-off-outline</v-icon>
        <strong>Video coming soon</strong>
        <p>In the meantime, read how Lulu works for your business and for your customers.</p>
        <button class="pill-btn" type="button" @click="selectTab('business')">
          <v-icon class="pill-btn__icon">mdi-book-open-page-variant-outline</v-icon>
          Read how it works
        </button>
      </div>

      <!-- The portrait clip sits beside the copy on wide screens and above it on narrow ones. -->
      <div v-else class="video-layout">
        <div class="video-frame" :style="{ '--video-aspect': videoAspect }">
          <video
            ref="inlineVideo"
            class="video-frame__video"
            :src="videoSrc"
            :poster="videoPoster || undefined"
            controls
            playsinline
            preload="metadata"
            @loadedmetadata="onInlineReady"
            @play="onInlinePlay"
            @error="onVideoError"
          >
            Your browser does not support embedded video.
          </video>
        </div>
        <div class="video-aside">
          <h3 class="video-aside__title">Three steps, no customer app</h3>
          <ul class="benefit-list">
            <li>
              <v-icon class="check-icon">mdi-check-circle</v-icon>
              <span>Customers scan your QR code and get a branded card in their wallet</span>
            </li>
            <li>
              <v-icon class="check-icon">mdi-check-circle</v-icon>
              <span>Your team adds stamps with the Lulu app at the till</span>
            </li>
            <li>
              <v-icon class="check-icon">mdi-check-circle</v-icon>
              <span>A full card unlocks the reward you've chosen</span>
            </li>
          </ul>
          <div class="video-toolbar">
            <button class="pill-btn" type="button" @click="expandVideo">
              <v-icon class="pill-btn__icon">mdi-arrow-expand</v-icon>
              Expand video
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- ── Business Tab ── -->
    <div v-if="activeTab === 'business'" class="tab-content">
      <p class="intro">
        Lulu gives your business a fully branded digital rewards card that lives directly in your
        customers' Apple Wallet or Google Wallet - no app required, no friction, just loyalty done right.
      </p>

      <h3><v-icon class="section-icon">mdi-rocket-launch-outline</v-icon> Getting started</h3>

      <div class="steps">
        <div class="step-card">
          <div class="step-badge">
            <v-icon class="step-icon-inner">mdi-account-plus-outline</v-icon>
            <span class="step-num">1</span>
          </div>
          <div class="step-body">
            <strong>Sign up and configure your card</strong>
            <p>After signing up, your branded digital rewards card is created - complete with your logo, colours, and reward structure. Everything is tailored to your business.</p>
          </div>
        </div>

        <div class="step-card">
          <div class="step-badge">
            <v-icon class="step-icon-inner">mdi-qrcode</v-icon>
            <span class="step-num">2</span>
          </div>
          <div class="step-body">
            <strong>Display your QR code at the counter</strong>
            <p>You'll receive a unique QR code to place at your till or counter. Customers simply scan it to register and instantly receive their branded rewards card.</p>
          </div>
        </div>

        <div class="step-card">
          <div class="step-badge">
            <v-icon class="step-icon-inner">mdi-cellphone-nfc</v-icon>
            <span class="step-num">3</span>
          </div>
          <div class="step-body">
            <strong>Stamp rewards cards with the Lulu app</strong>
            <p>Use the Lulu operator app (iOS) to scan a customer's rewards card each time they make a qualifying purchase. Stamps are added instantly and update live on their rewards card.</p>
          </div>
        </div>

        <div class="step-card">
          <div class="step-badge">
            <v-icon class="step-icon-inner">mdi-gift-outline</v-icon>
            <span class="step-num">4</span>
          </div>
          <div class="step-body">
            <strong>Reward your loyal customers</strong>
            <p>Once a customer reaches the required number of stamps, they're eligible for their reward. You decide what the reward is - a free coffee, a discount, whatever fits your business.</p>
          </div>
        </div>
      </div>

      <h3><v-icon class="section-icon">mdi-star-outline</v-icon> Why businesses choose Lulu</h3>

      <ul class="benefit-list">
        <li>
          <v-icon class="check-icon">mdi-check-circle</v-icon>
          <span><strong>Zero friction for customers</strong> - no app to download, no account to create on a third-party platform. Customers get their card in seconds.</span>
        </li>
        <li>
          <v-icon class="check-icon">mdi-check-circle</v-icon>
          <span><strong>Fully branded experience</strong> - your logo and colours on every card, so your brand stays front and centre in your customer's wallet.</span>
        </li>
        <li>
          <v-icon class="check-icon">mdi-check-circle</v-icon>
          <span><strong>Works on iPhone and Android</strong> - Apple Wallet and Google Wallet both supported out of the box.</span>
        </li>
        <li>
          <v-icon class="check-icon">mdi-check-circle</v-icon>
          <span><strong>Multiple operators</strong> - add team members to your account so any member of staff can stamp rewards cards using the Lulu app.</span>
        </li>
        <li>
          <v-icon class="check-icon">mdi-check-circle</v-icon>
          <span><strong>Simple, transparent pricing</strong> - a flat subscription per location, billed monthly or annually, with unlimited scans. No hidden fees, no per-stamp charges.</span>
        </li>
      </ul>

      <h3><v-icon class="section-icon">mdi-cellphone-check</v-icon> What the operator experience looks like</h3>
      <p class="body-text">
        Your staff open the Lulu app, tap to scan, and point the camera at the customer's rewards
        card. That's it - the stamp is applied immediately and the customer sees their updated rewards card
        without having to do anything.
      </p>
    </div>

    <!-- ── Customers Tab ── -->
    <div v-if="activeTab === 'customers'" class="tab-content">
      <p class="intro">
        Collect loyalty rewards from your favourite local businesses - all stored neatly inside your
        Apple Wallet or Google Wallet. No extra apps, no paper cards to lose.
      </p>

      <h3><v-icon class="section-icon">mdi-wallet-outline</v-icon> How to get your rewards card</h3>

      <div class="steps">
        <div class="step-card">
          <div class="step-badge">
            <v-icon class="step-icon-inner">mdi-qrcode-scan</v-icon>
            <span class="step-num">1</span>
          </div>
          <div class="step-body">
            <strong>Scan the QR code at the counter</strong>
            <p>When you visit a participating business, look for the Lulu QR code at the till or counter and scan it with your phone's camera.</p>
          </div>
        </div>

        <div class="step-card">
          <div class="step-badge">
            <v-icon class="step-icon-inner">mdi-account-edit-outline</v-icon>
            <span class="step-num">2</span>
          </div>
          <div class="step-body">
            <strong>Register in seconds</strong>
            <p>You'll be taken to a branded sign-up page for that business. Enter your details once and you're ready to go.</p>
          </div>
        </div>

        <div class="step-card">
          <div class="step-badge">
            <v-icon class="step-icon-inner">mdi-wallet-plus-outline</v-icon>
            <span class="step-num">3</span>
          </div>
          <div class="step-body">
            <strong>Add to your wallet</strong>
            <p>Your branded digital rewards card is instantly added to your Apple Wallet or Google Wallet. No app download needed - it lives right alongside your boarding passes and bank cards.</p>
          </div>
        </div>
      </div>

      <h3><v-icon class="section-icon">mdi-star-check-outline</v-icon> Collecting and redeeming rewards</h3>

      <ul class="benefit-list">
        <li>
          <v-icon class="check-icon">mdi-check-circle</v-icon>
          <span><strong>Get stamped at checkout</strong> - each time you visit and make a qualifying purchase, the business scans your rewards card to add a stamp. Just open your wallet and show your rewards card.</span>
        </li>
        <li>
          <v-icon class="check-icon">mdi-check-circle</v-icon>
          <span><strong>Track your progress on the rewards card</strong> - you can see exactly how many stamps you've collected directly on the rewards card inside your wallet. No login, no app, no fuss.</span>
        </li>
        <li>
          <v-icon class="check-icon">mdi-check-circle</v-icon>
          <span><strong>Earn your reward</strong> - once you've collected enough stamps, let the business know and claim your reward. Simple as that.</span>
        </li>
      </ul>

      <h3><v-icon class="section-icon">mdi-heart-outline</v-icon> Why you'll love it</h3>

      <ul class="benefit-list">
        <li>
          <v-icon class="check-icon">mdi-check-circle</v-icon>
          <span>No app to download - it works with the wallet already on your phone</span>
        </li>
        <li>
          <v-icon class="check-icon">mdi-check-circle</v-icon>
          <span>Always with you - your rewards card lives in your wallet, right next to everything else</span>
        </li>
        <li>
          <v-icon class="check-icon">mdi-check-circle</v-icon>
          <span>Works on iPhone and Android</span>
        </li>
        <li>
          <v-icon class="check-icon">mdi-check-circle</v-icon>
          <span>Stamp count visible at a glance, right on the rewards card</span>
        </li>
        <li>
          <v-icon class="check-icon">mdi-check-circle</v-icon>
          <span>One card per business, all in one place</span>
        </li>
      </ul>

      <h3><v-icon class="section-icon">mdi-credit-card-outline</v-icon> What your card looks like</h3>
      <img :src="RewardsCardExample" class="rewards-preview" />
    </div>

    <!-- ── Expanded video lightbox ── -->
    <v-dialog v-model="videoExpanded" :max-width="lightboxMaxWidth">
      <div class="video-lightbox" :style="{ '--video-aspect': videoAspect }">
        <div class="video-lightbox__bar">
          <span class="video-lightbox__title">How Lulu works</span>
          <button
            class="video-lightbox__close"
            type="button"
            aria-label="Close expanded video"
            @click="videoExpanded = false"
          >
            <v-icon class="video-lightbox__close-icon">mdi-close</v-icon>
          </button>
        </div>
        <video
          ref="expandedVideo"
          class="video-lightbox__video"
          :src="videoSrc"
          :poster="videoPoster || undefined"
          controls
          playsinline
          preload="metadata"
          @loadedmetadata="onExpandedReady"
          @error="onVideoError"
        >
          Your browser does not support embedded video.
        </video>
      </div>
    </v-dialog>
  </div>
</template>

<script>
import { ref, computed, watch, onBeforeUnmount } from "vue";
import RewardsCardExample from "@/assets/rewardsCardExample.jpeg";
import { track } from "@/analytics.js";

export default {
  props: {
    // Files under public/ are served from the site root, so the default
    // expects public/assets/how-lulu-works.mp4. A full URL works too.
    videoSrc: {
      type: String,
      default: "/assets/how-lulu-works.mp4",
    },
    // Still frame shown before playback starts. iOS Safari in particular
    // shows nothing until play without one.
    videoPoster: {
      type: String,
      default: "/assets/how-lulu-works-poster.jpg",
    },
  },
  setup(props) {
    const activeTab = ref("video");
    const videoUnavailable = ref(false);
    const videoExpanded = ref(false);
    const inlineVideo = ref(null);
    const expandedVideo = ref(null);
    let hasTrackedPlay = false;

    // Width divided by height, used by the CSS to size the player. Starts at
    // 9:16 to match the shipped portrait clip and corrects itself from the
    // file's own dimensions once its metadata loads.
    const videoAspect = ref("0.5625");
    const lightboxMaxWidth = computed(() =>
      Number(videoAspect.value) < 1 ? "560px" : "1280px"
    );

    const onInlineReady = () => {
      const video = inlineVideo.value;
      if (!video || !video.videoWidth || !video.videoHeight) return;
      videoAspect.value = String(video.videoWidth / video.videoHeight);
    };

    // Carried from the inline player into the lightbox so expanding picks
    // up exactly where the viewer was, in the same play state.
    let resumeAt = 0;
    let resumePlaying = false;

    const selectTab = (tab) => {
      if (activeTab.value === tab) return;
      activeTab.value = tab;
      track("how_lulu_works_tab_selected", { tab });
    };

    const onInlinePlay = () => {
      if (hasTrackedPlay) return;
      hasTrackedPlay = true;
      track("how_lulu_works_video_played", {});
    };

    const onVideoError = () => {
      console.warn(`[HowLuluWorks] video could not be loaded: ${props.videoSrc}`);
      videoUnavailable.value = true;
      videoExpanded.value = false;
    };

    const expandVideo = () => {
      const video = inlineVideo.value;
      resumeAt = video ? video.currentTime : 0;
      resumePlaying = Boolean(video && !video.paused && !video.ended);
      if (video) video.pause();
      videoExpanded.value = true;
      track("how_lulu_works_video_expanded", {});
    };

    const onExpandedReady = () => {
      const video = expandedVideo.value;
      if (!video) return;
      if (resumeAt > 0) video.currentTime = resumeAt;
      // play() can be refused without a fresh user gesture; the viewer can
      // then press play themselves.
      if (resumePlaying) video.play().catch(() => {});
    };

    // Closing the lightbox hands the playback position back to the inline player.
    watch(videoExpanded, (expanded) => {
      if (expanded) return;
      const expandedEl = expandedVideo.value;
      const inlineEl = inlineVideo.value;
      if (!expandedEl || !inlineEl) return;
      const wasPlaying = !expandedEl.paused && !expandedEl.ended;
      expandedEl.pause();
      inlineEl.currentTime = expandedEl.currentTime;
      if (wasPlaying) inlineEl.play().catch(() => {});
    });

    onBeforeUnmount(() => {
      [inlineVideo.value, expandedVideo.value].forEach((video) => {
        if (video) video.pause();
      });
    });

    return {
      activeTab,
      videoUnavailable,
      videoExpanded,
      inlineVideo,
      expandedVideo,
      videoAspect,
      lightboxMaxWidth,
      selectTab,
      onInlineReady,
      onInlinePlay,
      onVideoError,
      expandVideo,
      onExpandedReady,
      RewardsCardExample,
    };
  },
};
</script>

<style scoped>
.how-it-works {
  font-family: 'Roboto', sans-serif;
  font-size: 14px;
  line-height: 1.6;
  color: #333;
}

/* ── Tab bar ── */
.tab-bar {
  display: flex;
  border-bottom: 2px solid rgba(0, 109, 62, 0.2);
  margin-bottom: 24px;
}

.tab-btn {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 16px;
  background: none;
  border: none;
  border-bottom: 3px solid transparent;
  margin-bottom: -2px;
  font-size: 15px;
  font-weight: 600;
  color: #888;
  cursor: pointer;
  transition: color 0.2s, border-color 0.2s;
  font-family: 'Roboto', sans-serif;
}

.tab-btn:hover {
  color: rgb(0, 109, 62);
}

.tab-btn--active {
  color: rgb(0, 109, 62);
  border-bottom-color: rgb(0, 109, 62);
}

.tab-icon {
  font-size: 20px !important;
}

@media (max-width: 480px) {
  /* Three tabs don't fit side by side with icons, so stack icon over label. */
  .tab-btn {
    flex-direction: column;
    gap: 4px;
    font-size: 12px;
    padding: 10px 4px;
  }
}

/* ── Content ── */
.tab-content {
  max-width: 700px;
  margin: 0 auto;
}

.tab-content--video {
  max-width: 760px;
}

.intro {
  font-size: 14px;
  color: #555;
  margin-bottom: 20px;
  line-height: 1.75;
  border-left: 3px solid rgb(0, 109, 62);
  padding-left: 12px;
}

.body-text {
  color: #555;
  line-height: 1.7;
}

/* ── Shared pill button (expand, placeholder link) ── */
.pill-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px 18px;
  border-radius: 999px;
  border: 1.5px solid rgb(0, 109, 62);
  background: white;
  color: rgb(0, 109, 62);
  font-family: 'Roboto', sans-serif;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.2s, color 0.2s, box-shadow 0.2s;
}

.pill-btn:hover {
  background: rgb(0, 109, 62);
  color: white;
  box-shadow: 0 4px 16px rgba(0, 109, 62, 0.3);
}

.pill-btn__icon {
  font-size: 18px !important;
  color: inherit !important;
}

/* ── Video tab ── */
.video-layout {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: center;
  gap: 24px;
}

.video-frame {
  /* Height is the fixed side; width follows the clip's own aspect ratio, so a
     portrait clip stays narrow and a landscape one fills the row. */
  --frame-h: min(60vh, 420px);
  flex: 0 0 auto;
  width: min(100%, calc(var(--frame-h) * var(--video-aspect)));
  aspect-ratio: var(--video-aspect);
  background: #000;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.18);
}

.video-frame__video {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
  background: #000;
}

.video-aside {
  flex: 1 1 260px;
  min-width: 0;
}

/* Sits at the top of the panel, a touch larger than the other tabs' headings. */
.video-aside__title {
  font-size: 17px;
  margin-top: 0;
  margin-bottom: 14px;
}

.video-toolbar {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 4px;
}

.video-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 8px;
  padding: 40px 24px;
  background: rgba(0, 109, 62, 0.04);
  border: 1px dashed rgba(0, 109, 62, 0.3);
  border-radius: 12px;
}

.video-placeholder__icon {
  font-size: 40px !important;
  color: rgb(0, 109, 62) !important;
}

.video-placeholder strong {
  font-size: 15px;
  color: #1a1a1a;
}

.video-placeholder p {
  margin: 0;
  max-width: 360px;
  color: #555;
  font-size: 13px;
}

.video-placeholder .pill-btn {
  margin-top: 8px;
}

@media (max-width: 480px) {
  .video-frame {
    --frame-h: min(50vh, 360px);
  }

  .video-toolbar {
    flex-direction: column;
    align-items: stretch;
    text-align: center;
  }
}

/* ── Expanded video lightbox ── */
.video-lightbox {
  background: #000;
  border-radius: 12px;
  overflow: hidden;
}

.video-lightbox__bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 10px 8px 18px;
  background: rgba(255, 255, 255, 0.06);
}

.video-lightbox__title {
  color: white;
  font-family: 'Roboto', sans-serif;
  font-size: 15px;
  font-weight: 600;
}

.video-lightbox__close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px;
  background: none;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  transition: background 0.2s;
}

.video-lightbox__close:hover {
  background: rgba(255, 255, 255, 0.14);
}

.video-lightbox__close-icon {
  font-size: 22px !important;
  color: white !important;
}

.video-lightbox__video {
  display: block;
  width: 100%;
  max-height: 82vh;
  aspect-ratio: var(--video-aspect);
  background: #000;
  object-fit: contain;
}

/* ── Section headings ── */
h3 {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  font-weight: 700;
  color: rgb(0, 109, 62);
  margin-top: 28px;
  margin-bottom: 14px;
}

.section-icon {
  font-size: 18px !important;
  color: rgb(0, 109, 62) !important;
}

/* ── Step cards ── */
.steps {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.step-card {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  background: rgba(0, 109, 62, 0.04);
  border: 1px solid rgba(0, 109, 62, 0.12);
  border-radius: 10px;
  padding: 14px 16px;
}

.step-badge {
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  background: rgb(0, 109, 62);
  border-radius: 50%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.step-icon-inner {
  font-size: 18px !important;
  color: white !important;
}

.step-num {
  display: none;
}

.step-body {
  flex: 1;
}

.step-body strong {
  display: block;
  font-size: 14px;
  color: #1a1a1a;
  margin-bottom: 4px;
}

.step-body p {
  margin: 0;
  color: #555;
  font-size: 13px;
  line-height: 1.6;
}

/* ── Benefit list ── */
.benefit-list {
  list-style: none;
  padding: 0;
  margin: 0 0 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.benefit-list li {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  color: #333;
}

.check-icon {
  font-size: 18px !important;
  color: rgb(0, 109, 62) !important;
  flex-shrink: 0;
  margin-top: 1px;
}

/* ── Card preview ── */
.rewards-preview {
  width: 30%;
  height: auto;
  display: block;
  margin-top: 12px;
  border-radius: 10px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
}

@media (max-width: 480px) {
  .rewards-preview {
    width: 60%;
  }
}
</style>
